import { cn } from '@/lib/cn';

/**
 * Inline animated spinner. Used inline with text in buttons + status
 * lines to give the user a moving signal that work is in progress —
 * a static "Loading…" / "Saving…" string isn't enough by itself
 * because the user can't tell whether the page froze or whether the
 * server's just slow.
 *
 * Sized via Tailwind's h/w classes (default `h-4 w-4` matches text
 * cap height). Color inherits from the text color so the spinner
 * looks right on every button variant without per-call tweaking.
 */
export function Spinner({
  className = 'h-4 w-4',
  label = 'Loading',
}: {
  className?: string;
  /** Screen-reader-only label so assistive tech announces something
   * happening. Visible callers usually pair this with their own text
   * ("Saving…" beside the spinner) so the announcement still makes
   * sense. */
  label?: string;
}) {
  return (
    <svg
      className={cn('animate-spin', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label={label}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
